import React, { useEffect, useState, useMemo } from "react";
import { PortfolioItem } from "@/hooks/useProfile";
import { CaseStudyItem } from "@/types/case-study";
import { UnifiedPortfolioItem, portfolioItemToUnified, caseStudyToUnified } from "@/types/unified-portfolio";
import GalleryItem from "./portfolio/GalleryItem";
import CaseStudyGalleryItem from "./portfolio/CaseStudyGalleryItem";
import { useIsMobile } from "@/hooks/use-mobile";
import PortfolioItemModal from "./PortfolioItemModal";
import MobilePortfolioModal from "./portfolio/modal/MobilePortfolioModal";
import { supabase } from "@/integrations/supabase/client";
import { convertSupabaseUrlToRenderUrl } from "@/utils/imageUtils";
import { useRouter } from "next/navigation";
import { recordCaseStudyView, canRecordCaseStudyView } from "@/services/caseStudy/recordCaseStudyView";

function slugify(text: string) {
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "")
    .replace(/--+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");
}

function getPortfolioSlug(item: PortfolioItem) {
  return item.slug || slugify(item.title || item.id);
}

function findItemBySlug(items: PortfolioItem[], slug: string) {
  return items.find((item) => getPortfolioSlug(item) === slug);
}

// Helper for rate limiting: only one view per item per session per hour
function canRecordPortfolioView(itemId: string): boolean {
  // Guard against SSR - localStorage is not available on the server
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return false;
  }

  const key = `portfolio-view-${itemId}`;
  const lastView = localStorage.getItem(key);
  const now = Date.now();
  if (lastView && now - parseInt(lastView) < 60 * 60 * 1000) {
    return false;
  }
  localStorage.setItem(key, now.toString());
  return true;
}

async function recordPortfolioItemView(
  profileId: string,
  itemId: string,
  viewerId: string | null = null
) {
  try {
    await supabase.from("portfolio_item_views" as any).insert([
      {
        profile_id: profileId,
        portfolio_item_id: itemId,
        viewer_id: viewerId,
      },
    ]);
  } catch (error) {
    console.error("Error recording portfolio item view:", error);
  }
}

interface UnifiedPortfolioGalleryProps {
  username: string;
  username_slug: string;
  portfolioItems: PortfolioItem[];
  caseStudies: CaseStudyItem[];
  profile: {
    id: string;
    avatar_url?: string | null;
    profession?: string | null;
    display_name?: string | null;
  };
  imageLoadStatus?: Record<string, "loading" | "success" | "error">;
  openPortfolioModal?: PortfolioItem | null;
  onClosePortfolioModal?: () => void;
}

const UnifiedPortfolioGallery = ({
  username,
  username_slug,
  portfolioItems,
  caseStudies,
  profile,
  imageLoadStatus = {},
  openPortfolioModal,
  onClosePortfolioModal,
}: UnifiedPortfolioGalleryProps) => {
  const router = useRouter();
  const isMobile = useIsMobile();
  
  // Modal state (local unless controlled from above)
  const [selectedItem, setSelectedItem] = useState<PortfolioItem | null>(null);

  // Convert both types to unified items and combine them
  const unifiedItems = useMemo(() => {
    const portfolioUnified = portfolioItems.map(portfolioItemToUnified);
    const caseStudyUnified = caseStudies.map(caseStudyToUnified);
    
    // Combine and sort with featured items first
    const combined = [...portfolioUnified, ...caseStudyUnified];
    const sorted = combined.sort((a, b) => {
      // Featured items always go first
      if (a.is_featured && !b.is_featured) return -1;
      if (!a.is_featured && b.is_featured) return 1;
      
      // Then sort by order (or created_at if no order)
      if (a.order !== undefined && b.order !== undefined) {
        return a.order - b.order;
      }
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
    
    return sorted;
  }, [portfolioItems, caseStudies]);

  // Open modal and update SEO-friendly URL
  const openModal = (item: PortfolioItem) => {
    setSelectedItem(item);
    const slug = getPortfolioSlug(item);
    window.history.pushState({}, "", `/u/${username_slug}/portfolio/${slug}`);
    // Record a view (rate limited)
    if (profile && canRecordPortfolioView(item.id)) {
      recordPortfolioItemView(profile.id, item.id, null);
    }
  };

  // Close modal and revert to profile URL
  const closeModal = () => {
    setSelectedItem(null);
    window.history.replaceState({}, "", `/u/${username_slug}`);
  };

  // Handle case study click
  const handleCaseStudyClick = (caseStudy: CaseStudyItem) => {
    // Record view before navigation
    if (profile && canRecordCaseStudyView(caseStudy.id)) {
      recordCaseStudyView(profile.id, caseStudy.id, null);
    }
    const slug = caseStudy.slug || slugify(caseStudy.title);
    router.push(`/case-study/${slug}`);
  };

  // On mount, check for SEO-friendly modal URL and open modal if present
  useEffect(() => {
    if (openPortfolioModal) return; // Don't auto-open if controlled from above
    const match = window.location.pathname.match(
      /^\/u\/([^/]+)\/portfolio\/([^/]+)/
    );
    if (match) {
      const slug = match[2];
      const item = findItemBySlug(portfolioItems, slug);
      if (item) setSelectedItem(item);
    }
    // Listen for popstate to close modal if user presses back
    const onPopState = () => {
      const match = window.location.pathname.match(
        /^\/u\/([^/]+)\/portfolio\/([^/]+)/
      );
      if (!match) {
        setSelectedItem(null);
      } else {
        const slug = match[2];
        const item = findItemBySlug(portfolioItems, slug);
        if (item) setSelectedItem(item);
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [portfolioItems, username_slug, openPortfolioModal]);

  // Generate aspect ratio classes based on position in the grid
  const getAspectRatioClass = (item: UnifiedPortfolioItem, index: number): string => {
    if (isMobile) {
      return "aspect-[16/9]";
    }
    if (index === 0 && item.is_featured) {
      return "aspect-[16/9]";
    } else {
      return "aspect-[16/9]";
    }
  };

  const getYearFromItem = (item: UnifiedPortfolioItem): string => {
    if (item.created_at) {
      return new Date(item.created_at).getFullYear().toString();
    }
    if (item.updated_at) {
      return new Date(item.updated_at).getFullYear().toString();
    }
    return new Date().getFullYear().toString();
  };

  // Use controlled modal if provided, else local state
  const modalItem =
    openPortfolioModal !== undefined ? openPortfolioModal : selectedItem;
  const handleClose = onClosePortfolioModal || closeModal;

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
        {unifiedItems.map((item, index) => {
          const itemId = item.id || item.title || `item-${index}`;
          const uniqueKey = `${itemId}-${index}`;
          const yearText = getYearFromItem(item);
          const aspectRatioClass = getAspectRatioClass(item, index);
          const gridClass =
            !isMobile && index === 0 && item.is_featured ? "md:col-span-2" : "";
          const shouldShowBlur = !isMobile && index === 0 && item.is_featured;

          if (item.type === "shot") {
            // Convert back to PortfolioItem for existing GalleryItem
            const portfolioItem = portfolioItems.find(p => p.id === item.id);
            if (!portfolioItem) return null;

            const imageUrl =
              portfolioItem.images && portfolioItem.images.length > 0
                ? convertSupabaseUrlToRenderUrl(portfolioItem.images[0].url)
                : convertSupabaseUrlToRenderUrl(portfolioItem.image_url);
            const imageCount = portfolioItem.images ? portfolioItem.images.length : 1;
            const hasMultipleImages = imageCount > 1;

            return (
              <div key={uniqueKey} className={`${gridClass}`}>
                <GalleryItem
                  item={portfolioItem}
                  itemId={itemId}
                  imageUrl={imageUrl}
                  hasMultipleImages={hasMultipleImages}
                  imageCount={imageCount}
                  imageLoadStatus={imageLoadStatus}
                  aspectRatioClass={aspectRatioClass}
                  yearText={yearText}
                  usernameSlug={username_slug}
                  onOpenModal={openModal}
                  shouldShowBlur={shouldShowBlur}
                />
              </div>
            );
          } else {
            // Case study item
            const caseStudy = caseStudies.find(cs => cs.id === item.id);
            if (!caseStudy) return null;

            return (
              <div key={uniqueKey} className={`${gridClass}`}>
                <CaseStudyGalleryItem
                  caseStudy={caseStudy}
                  aspectRatioClass={aspectRatioClass}
                  yearText={yearText}
                  usernameSlug={username_slug}
                  onClick={() => handleCaseStudyClick(caseStudy)}
                  shouldShowBlur={shouldShowBlur}
                />
              </div>
            );
          }
        })}
      </div>
      {modalItem &&
        (isMobile ? (
          <MobilePortfolioModal
            item={modalItem}
            isOpen={!!modalItem}
            onClose={handleClose}
            username={username}
            username_slug={username_slug}
            profile={profile}
          />
        ) : (
          <PortfolioItemModal
            item={modalItem}
            isOpen={!!modalItem}
            onClose={handleClose}
            username={username}
            username_slug={username_slug}
          />
        ))}
    </>
  );
};

export default UnifiedPortfolioGallery;
